// Original Overlay169 structured slide layer. Host and native share identical wrappers.
import React,{useEffect,useLayoutEffect,useRef,useState} from 'react';
import type {LayoutBaseProps} from '../msb/layouts';
import {spring,interpolate,useVideoConfig} from 'remotion';
import {SLIDE_COMPONENTS,EXTRA_SLIDE_COMPONENTS,slideComponentOf} from '../msb/slide-components';
import {MOTION_STAGES} from '../msb/motion-stages';
import {slideWipe} from '../msb/motion';
import {PRESENTER_HOST} from '../../lib/msb/avatar-catalog';
import {CornerPeel,SpeedLines,slideRush} from './SlideAccents';
import {briefStageContent,briefGlueText} from '../msb/motion-brief-shape';
import {normalizeAccent} from '../../lib/motion-kit/oneshape/data';
import {SLIDE_COLORS_BY_SKIN,normHexColor,readableTextOn} from '../../lib/templates/overlay-caption-skin';
import {vmPanelTitle} from '../../lib/templates/vm-side-panel-title';
import {vmPanelLayout,vmPanelScrim} from './vm-side-panel-layout';
import {FW} from '../msb/tokens';
import {motionStageFromKey} from '../../lib/templates/motion-stage-family';
import {validSlideWordCues} from '../../lib/templates/word-cues';
import type {SourceSlide} from '../../../apps/precision-editor/protocol/source-slide';
import {slideCaptionFit,type SlideContentExtent} from '../../lib/templates/overlay169-caption-geometry';
import {safeInsetPx} from '../../lib/safe-zone';
import {listifySlashTitle} from '../../lib/templates/slide-slash-list';
import {SlideContentFitContext,SlideRoomContext,SLIDE_ROOM_MAX,type SlideRoomLevel} from '../msb/slide-caption-fit';
import {EngineWordNodesContext,useEngineWordNodes} from '../msb/engine-word-nodes';
import {CONSTRUCTION_SLIDE_TYPES} from '../../lib/templates/slide-kind-contract';
/** 📐 #6119 자막 띠 위에 앉히는 장표 — 포스터는 그림이 화면 바탕이라 제외(줄이면 가장자리가 빈다). 모션 타이포는 글자 칸에만 건다. */
const FIT_SKIP_TYPES:ReadonlySet<string>=new Set(['posterStack']);
/** 배치가 없는 곳(서버 렌더 · jsdom 시험)은 재지 않는다 — 정적 HTML 시험에 useLayoutEffect 경고를 남기지 않는다(종전 HTML 그대로). */
const useMeasureEffect=typeof window==='undefined'||/jsdom/i.test(typeof navigator==='undefined'?'':navigator.userAgent)?useEffect:useLayoutEffect;
/** 모션 타이포 카메라(kinetic-typo · 1→1.06 · 원점 y 45%) — 그 확대 뒤에도 윗선 위가 되게 거꾸로 잡는다. */
const KINETIC_PUSH={scale:1.06,originY:0.45};
/** 글자 덩어리 배치 상자(변환 무시 · `box` 기준 px). 화면을 거의 채우는 칸(바탕·겉상자)은 건너뛰고 그 안을 잰다. */
function contentExtent(box:HTMLElement):SlideContentExtent|null{
 const H=box.offsetHeight;let top=Infinity,bottom=-Infinity,left=Infinity,right=-Infinity;
 for(const el of box.querySelectorAll('*')){
  if(!(el instanceof HTMLElement)||el.offsetWidth<=0||el.offsetHeight<=0||el.offsetHeight>=H*0.9)continue;
  let x=0,y=0,e:Element|null=el;
  while(e&&e!==box&&e instanceof HTMLElement){x+=e.offsetLeft;y+=e.offsetTop;e=e.offsetParent;}
  if(e!==box)continue;
  top=Math.min(top,y);bottom=Math.max(bottom,y+el.offsetHeight);left=Math.min(left,x);right=Math.max(right,x+el.offsetWidth);
 }
 return bottom>top?{top,bottom,left,right}:null;
}
/** '#RRGGBB' → [r,g,b] · 못 읽으면 null. */
const hexRgb=(v:unknown):[number,number,number]|null=>{const h=normHexColor(v);return h?[parseInt(h.slice(1,3),16),parseInt(h.slice(3,5),16),parseInt(h.slice(5,7),16)]:null;};
export function Overlay169SlideLayer({data,f}:{data:SourceSlide;f:number}){
 const {width,height,fps}=useVideoConfig();
 // 📐 #6393 큰 제목 칸에 « / » 로 이어 붙은 여러 항목은 목록 장표로 그린다(넘칠 때만 · 글자 그대로 · 저장 데이터는 그대로 — `slide-slash-list.ts`).
 //   앞 장면(모션 무대 변신 출발점)도 같은 규칙을 받아야 도형이 «그린 모양»에서 출발한다. 해당 없으면 같은 객체 = 종전 그대로.
 const slideData=listifySlashTitle(data.slide),slideBeats=data.beats,c=data.context.prevSlide?{...data.context,prevSlide:listifySlashTitle(data.context.prevSlide)}:data.context;
 // ⏱🔤 낱말 신호(2026-10-04 · 선택 칸) — 없으면(정밀 편집·옛 페이로드) 종전 시간표 그대로.
 //   ★지문 검사★ — 정밀 편집에서 장표 글을 고치면 옛 신호가 남은 글에 붙는다 → 지금 글의 지문과 다르면 버린다.
 const wordCues=slideData?validSlideWordCues(data.wordCues,slideData.type,slideData.content):undefined,cueProps={...(wordCues?.items?{itemCues:wordCues.items}:{}),
  // 🧍✅ 상반신 진행자 구도(오른쪽 비움 = PRESENTER_HOST.reservePct)면 체크리스트는 «말하는 순간 체크가 켜짐»(낱말 신호 · 없으면 문장 시간표) — 그 밖은 종전.
  ...(data.context.reserveRightPct===PRESENTER_HOST.reservePct&&slideData?.type==='checkList'?{tickOnCue:true}:{})};
 // 🏗 [건설] «자기 패널» 장표(사고 개요·예방대책·TBM) — 바탕을 스스로 정한다(`construction-slides.tsx`). 딤·VM 축소 상자·모션 무대·
 //   쓸림·돌진·속도선·모서리 접힘·자막 맞추기(#6119) 없이 화면 전체에 얹고, 자막 윗선은 장표가 직접 받아 그 위에 앉는다.
 //   ★종전 갈래는 `SlideComp` 가 비어 저절로 빠진다★(조건식은 한 글자도 안 바꾼다) — 이 셋은 `SelfPanelComp` 한 갈래로만 그린다.
 //   앞 장면이 이 종류면 모션 무대의 «앞 장표에서 변신»·«나가는 무대»도 걸지 않는다(무대 도형이 없는 장표다).
 const selfPanel=!!slideData&&CONSTRUCTION_SLIDE_TYPES.has(slideData.type);
 const SlideComp=slideData&&!selfPanel?slideComponentOf(slideData.type) as unknown as React.ComponentType<LayoutBaseProps>|undefined:undefined; // 🌅 공용 → 아침 조회 틀 전용 순
 const SelfPanelComp=slideData&&selfPanel?SLIDE_COMPONENTS[slideData.type] as React.ComponentType<LayoutBaseProps&{captionTopPx?:number;reserveRightPx?:number}>:undefined;
 const isMotionBrief=c.motionBrief,slideMotionKit=c.motionKit,slideColors=c.colors;
 // 🧊🗂 모션 무대 가족 — 어느 무대로 그릴지는 표가 정한다(`motion-stage-family.ts`). 키가 없거나 모르는 값이면 모션 브리핑 무대(옛 페이로드·늦게 배포된 앱).
 const stageDef=motionStageFromKey(c.motionStage),{Stage:MotionStage,Exit:MotionStageExit,contentColors:stageContentColors}=MOTION_STAGES[stageDef.stage],stageAccent=SLIDE_COLORS_BY_SKIN[stageDef.skin]?.accent;
 const briefAccent=normalizeAccent(slideColors.accent)??stageAccent??slideColors.accent;
 const briefColors={...slideColors,accent:briefAccent};
 const briefBrand=normalizeAccent(slideColors.accent)?slideColors.accent:stageAccent??slideColors.accent;
 const briefCur=slideData?{type:slideData.type,content:briefStageContent(slideData.type,{...slideData.content,...(slideData.type==='kineticTypo'?{beatTexts:slideBeats.map(b=>b.text)}:{})})}:null;
 const prev=c.prevSlide&&!CONSTRUCTION_SLIDE_TYPES.has(c.prevSlide.type)?c.prevSlide:null;
 const briefPrev=prev?{type:prev.type,content:briefStageContent(prev.type,{...prev.content,...(prev.beatTexts?{beatTexts:prev.beatTexts}:{})})}:null;
 const briefExitPrev=isMotionBrief&&!slideData?briefPrev:null;
 const briefSceneFrames=c.sceneFrames,briefCaption=c.caption,briefPrevCaption=c.prevCaption;
 const presenterClipUrl=c.reserveRightPct>0,PRESENTER_RESERVE_PCT=c.reserveRightPct;
 const isFirstSlideScene=c.first,isLastSlideScene=c.last;
 const rounded=Math.max(0,Math.round(f));
 const rush=c.first?slideRush(rounded):null,ovOpacity=interpolate(spring({frame:f,fps,durationInFrames:16,config:{damping:13}}),[0,1],[0,1]);
 const wipe=slideMotionKit&&!isMotionBrief?slideWipe(rounded,fps):null,wipeWindow=!!wipe&&rounded<16;
 const slideKitProps=slideMotionKit||isMotionBrief?{motionKit:true}:{};
 const imgLocalFrame=f,activeSceneArrIdx=c.opening?0:1;
 // 📐 #6119 자막 띠 위에 앉히기 — 옛 설계도 가족 등 전체 화면 장표(모션 무대·VM 한쪽 패널 밖)는 장면에 자막이 있으면 호스트가 자막 윗선을 싣는다(`c.caption`).
 //   실제 배치를 재서(변환 무시 · 매 렌더 · 같은 프레임 = 같은 값) 글자 덩어리가 그 윗선에 닿을 때만 줄이거나 올린다(`slideCaptionFit`).
 //   안 닿으면 null → 감싸는 칸도 없다 = 종전 DOM 그대로. 잴 자리 = 딤 칸의 다음 형제(프레젠터면 그 안 · 감쌌으면 그 안).
 const isKinetic=slideData?.type==='kineticTypo';
 // 🌅 아침 조회 틀 전용 장표(EXTRA)는 자막 띠(하단) 위 고정 배치로 그려져 있다(정본 v3 · 카드 아래끝 ≈765) — 맞추기로 줄이지 않는다(10-11 실측: 사고·두 가지 장면이 작게 줄던 결함).
 const fitCaptionTop=!isMotionBrief&&!c.vmPanel&&slideData&&SlideComp&&!FIT_SKIP_TYPES.has(slideData.type)&&!Object.prototype.hasOwnProperty.call(EXTRA_SLIDE_COMPONENTS,slideData.type)&&c.caption?c.caption.top:undefined;
 const fitKey=fitCaptionTop===undefined?'':`${slideData?.type}|${c.sceneIndex??''}|${fitCaptionTop}|${presenterClipUrl?1:0}|${width}x${height}`;
 const dimRef=useRef<HTMLDivElement>(null);
 // room = «자리 짜기» 단계(slide-caption-fit) — 줄여야 들어갈 때만 한 단계씩 올려 다시 잰다(같은 장면 안에서만 · 장면이 바뀌면 0부터).
 const [fit,setFit]=useState<{key:string;room:SlideRoomLevel;transform:string|null}|null>(null);
 const [,setFontsTick]=useState(0);
 useMeasureEffect(()=>{
  if(!fitKey){if(fit)setFit(null);return;}
  let box=dimRef.current?.nextElementSibling??null;
  if(box&&presenterClipUrl)box=box.firstElementChild;
  if(box&&box.hasAttribute('data-slide-caption-fit'))box=box.firstElementChild;
  if(isKinetic)box=box?.querySelector('[data-kinetic-title]')?.parentElement??null;
  const extent=box instanceof HTMLElement?contentExtent(box):null;
  const S=width/1920,safe=safeInsetPx('16:9',width,height,'core');
  const r=extent&&fitCaptionTop!==undefined?slideCaptionFit({extent,captionTop:fitCaptionTop,safeTop:safe.top,gap:24*S,pad:0.018*(extent.bottom-extent.top)+6*S,
   ...(isKinetic?{push:{scale:KINETIC_PUSH.scale,originY:KINETIC_PUSH.originY*height}}:{})}):null;
  const room:SlideRoomLevel=fit&&fit.key===fitKey?fit.room:0;
  // 글자를 줄여야만 들어가면(k<1) 먼저 자리 짜기를 한 단계 올리고 다음 그림에서 다시 잰다. 마지막 단계에서도 넘치면 그때 줄인다.
  if(r&&r.k<1&&room<SLIDE_ROOM_MAX){const up={key:fitKey,room:(room+1) as SlideRoomLevel,transform:null};setFit(prev=>prev?.key===up.key&&prev.room===up.room&&prev.transform===null?prev:up);return;}
  const next=r||room>0?{key:fitKey,room,transform:r?`translate(${r.tx}px, ${r.ty}px) scale(${r.k})`:null}:null;
  setFit(prev=>prev?.key===next?.key&&prev?.room===next?.room&&prev?.transform===next?.transform?prev:next);
 });
 // 웹 글꼴이 늦게 오면 배치가 바뀐다 — 멈춘 미리보기도 한 번 다시 잰다.
 useMeasureEffect(()=>{if(!fitKey||typeof document==='undefined'||!document.fonts?.ready)return;let alive=true;document.fonts.ready.then(()=>{if(alive)setFontsTick(t=>t+1);});return()=>{alive=false;};},[fitKey]);
 const fitTransform=fit&&fitKey&&fit.key===fitKey?fit.transform:null;
 const fitRoom:SlideRoomLevel=fit&&fitKey&&fit.key===fitKey?fit.room:0;
 const roomed=(node:React.ReactNode)=>fitRoom>0?<SlideRoomContext.Provider value={fitRoom}>{node}</SlideRoomContext.Provider>:node;
 const fitted=(node:React.ReactNode)=>!fitTransform?roomed(node):isKinetic
  ?<SlideContentFitContext.Provider value={fitTransform}>{roomed(node)}</SlideContentFitContext.Provider>
  :<div data-slide-caption-fit="" style={{position:"absolute",inset:0,transform:fitTransform,transformOrigin:"0 0"}}>{roomed(node)}</div>;
 // 📐 #6431 받기 엔진이 가운데 정렬 3줄 이상 글의 가운데 줄을 앞 줄 왼쪽 끝에 그린다 — 그런 글만 어절마다 따로 노드로(`engine-word-nodes.ts`).
 //   표식은 display:none(엔진이 건너뜀 · 그림 0) · 걸린 글이 없으면 값 null = 종전 그대로.
 const [engineWordsRef,engineWords]=useEngineWordNodes();
 return <EngineWordNodesContext.Provider value={engineWords}>
      <span ref={engineWordsRef} style={{display:"none"}} />
      {slideData && SelfPanelComp && (
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <SelfPanelComp colors={slideColors} f={Math.max(0, Math.round(imgLocalFrame))} {...slideData.content} {...(slideBeats ? { beats: slideBeats } : {})} {...cueProps}
            captionTopPx={c.vmPanel?.captionTop ?? c.caption?.top} reserveRightPx={presenterClipUrl ? Math.round(width * PRESENTER_RESERVE_PCT / 100) : 0} />
        </div>
      )}
      {slideData && SlideComp && isMotionBrief && (
        /* 🧊 모션 무대(표가 고른 무대 · 모션 브리핑 = 캔버스 + 변신하는 도형 하나 + 도형 안 장표). 앞 장면도 장표면 캔버스는 끊기지 않고,
           아니면 무대가 전환 길이로 캔버스를 밝힌다(MotionBriefStage). 딤·돌진·쓸림은 쓰지 않는다. */
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <MotionStage
            f={Math.max(0, Math.round(imgLocalFrame))}
            slide={briefCur ?? { type: slideData.type, content: slideData.content }}
            prevSlide={briefPrev}
            opening={activeSceneArrIdx === 0}
            sceneFrames={briefSceneFrames}
            canvas={slideColors.bg}
            brand={briefBrand}
            reserveRightPx={presenterClipUrl ? Math.round(width * PRESENTER_RESERVE_PCT / 100) : 0}
            captionTopPx={briefCaption?.top}
            captionBottomPx={briefCaption?.bottom}
            captionWidthPx={briefCaption?.width}
            prevCaption={briefPrevCaption}
            titleColors={briefColors ?? slideColors}
            sceneIndex={c.sceneIndex}
            wordCues={wordCues}
          >
            <SlideComp colors={stageContentColors(slideData.type, briefColors ?? slideColors, briefBrand, c.sceneIndex)} f={Math.max(0, Math.round(imgLocalFrame))} {...briefStageContent(slideData.type, slideData.content)} {...(slideBeats ? { beats: slideBeats.map((b) => ({ ...b, text: briefGlueText(b.text) })) } : {})} {...cueProps} {...slideKitProps} />
          </MotionStage>
        </div>
      )}
      {briefExitPrev && (
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <MotionStageExit f={Math.max(0, Math.round(imgLocalFrame))} prevSlide={briefExitPrev} canvas={slideColors.bg} brand={briefBrand}
            reserveRightPx={0} prevCaption={briefPrevCaption} split sceneIndex={c.sceneIndex} />
        </div>
      )}
      {slideData && SlideComp && !isMotionBrief && (
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          // 🚀 첫 장표 씬 도입 — 덩어리째 소실점에서 다가온다(0.8초 뒤 정확히 제자리).
          opacity: wipeWindow ? (rush ? rush.opacity : 1) : rush ? ovOpacity * rush.opacity : ovOpacity,
          ...(rush ? { transform: rush.transform } : {}),
        }}>
          {/* ④ kit — 장표 본문을 «아래에서 올라오는 창» 안에 둔다(창이 다 열리면 전체 화면 = 종전과 같은 자리). */}
          {(() => { const vm = c.vmPanel; const slideBody = vm ? (() => {
            /* 🎬🗂 영상 위 장표(VM) 한쪽 패널(2026-10-07 · `lib/templates/vm-side-panel.ts` 가 작업물 단위로 고른 장면만) —
               화면 전체 딤 대신 패널 쪽만 짙은 그라데이션 한 겹 · 장표는 축소한 가상 화면에 그대로(장표 10종 무수정) · 작은 색 꼬리표.
               프레젠터가 오른쪽을 쓰면 왼쪽 패널. 계산은 `vm-side-panel-layout.ts`(참고 영상 실측값). */
            const side = presenterClipUrl ? 'left' as const : 'right' as const;
            const shown = vmPanelTitle(slideData.content.title, vm.tag);
            const content = shown.title === slideData.content.title ? slideData.content : { ...slideData.content, title: shown.title };
            const L = vmPanelLayout({ width, height, side, hasTag: !!shown.tag, captionTop: vm.captionTop, slide: { type: slideData.type, content } });
            const rgb = hexRgb(slideColors.bg) ?? (slideColors.isDark === false ? [244, 247, 251] as [number, number, number] : [8, 10, 18] as [number, number, number]);
            return (<>
              <div data-vm-panel={side} style={{ position: "absolute", inset: 0, background: vmPanelScrim(side, rgb) }} />
              {L.tag && shown.tag ? (
                <div data-vm-panel-tag="" style={{ position: "absolute", left: L.tag.left, top: L.tag.top, padding: `${L.tag.padY}px ${L.tag.padX}px`, borderRadius: L.tag.radius,
                  background: slideColors.accent, color: readableTextOn(slideColors.accent, "#FFFFFF"), fontSize: L.tag.fontPx, fontWeight: FW.bold, lineHeight: 1.2, whiteSpace: "nowrap" }}>{shown.tag}</div>
              ) : null}
              <div data-vm-panel-frame="" style={{ position: "absolute", left: L.frame.left, top: L.frame.top, width: L.frame.width, height: L.frame.height, transform: `scale(${L.scale})`, transformOrigin: "0 0" }}>
                <SlideComp colors={slideColors} f={Math.max(0, Math.round(imgLocalFrame))} {...content} anchor={L.anchor} {...(slideBeats ? { beats: slideBeats } : {})} {...cueProps} {...slideKitProps} />
              </div>
            </>);
          })() : (<>
          {/* 📐 설계도=딤 완화(회장님 07-17 검정 프리뷰 수리) — 브리핑극장 딤(0.84)은 밝은 실사 배경
              전제. 설계도의 어두운 결정론 배경 위에 겹치면 화면 전체가 죽는다(실측). 배경 그라디언트가
              은은히 비치는 수준으로만 눌러 장표 패널과 깊이를 분리한다. */}
          <div ref={dimRef} style={{ position: "absolute", inset: 0, background: c.dim }} />
          {presenterClipUrl ? (
            /* 🧑‍🏫 프레젠터 예약 컬럼(MSB composition 미러) — 장표 콘텐츠를 좌측 박스로 몰아 우측 아바타
               자리를 비운다(레이아웃 % 좌표는 이 박스 기준 재해석 → 겹침 없이 자연 축소). */
            <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, right: `${PRESENTER_RESERVE_PCT}%` }}>
              {fitted(<SlideComp colors={slideColors} f={Math.max(0, Math.round(imgLocalFrame))} {...slideData.content} {...(slideBeats ? { beats: slideBeats } : {})} {...cueProps} {...slideKitProps} />)}
            </div>
          ) : (
            fitted(<SlideComp colors={slideColors} f={Math.max(0, Math.round(imgLocalFrame))} {...slideData.content} {...(slideBeats ? { beats: slideBeats } : {})} {...cueProps} {...slideKitProps} />)
          )}
          </>);
          return wipe ? (
            <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, top: `${((1 - wipe.p) * 100).toFixed(3)}%`, overflow: "hidden" }}>
              <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height }}>{slideBody}</div>
            </div>
          ) : slideBody; })()}
          {wipe?.active && (
            /* ④ 쓸림 경계 띠 — 드러나는 선 바로 위를 액센트가 따라 올라간다(다 열리면 화면 위로 빠진다). */
            <div style={{ position: "absolute", left: 0, right: 0, height: Math.round(height * 0.03),
              top: Math.round((1 - wipe.p) * height - height * 0.03), background: slideColors.accent, opacity: 0.9 * wipe.bandOpacity }} />
          )}
          {/* 💨 속도선 — 돌진과 같은 구간에서만 살고 장표가 서기 전에 사라진다(글자 위에 남으면 소음). */}
          {isFirstSlideScene && (
            <SpeedLines f={Math.max(0, Math.round(imgLocalFrame))} color={slideColors.accent} width={width} height={height} />
          )}
          {/* 📄 모서리 접힘 — 마지막 장표 씬 우하단. 장표 위에 얹는 구두점. */}
          {isLastSlideScene && (
            <CornerPeel f={Math.max(0, Math.round(imgLocalFrame))} width={width} height={height}
              page={slideColors.panel} under={slideColors.bg} edge={slideColors.accent} />
          )}
        </div>
      )}
 </EngineWordNodesContext.Provider>;
}
